import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import {
  createWechatPermanentEntryIssue,
  createWechatPermanentEntryViewerSession,
  getPublicWechatPermanentEntry,
  getWechatPermanentEntryIssue,
  type PublicWechatPermanentEntry,
  type WechatPermanentEntryIssue,
} from '../lib/wechat-permanent-entry-api';
import { useWebLanguage } from '../lib/web-language';
import { isYoutuAgentProfile, YOUTU_BRAND_NAME } from '../lib/youtu-brand';
import NiuboshiExpertSharePage, { getExpertShareConfigByPublicToken } from './NiuboshiExpertSharePage';
import './WechatPermanentEntryPage.css';

type IssueStage = 'idle' | 'creating' | 'pending' | 'confirmed' | 'expired' | 'revoked' | 'error';
type PageErrorKey = 'missingToken' | 'loadFailed' | 'viewerNotReady';

const POLL_INTERVAL_MS = 2000;
const QR_READY_POLL_INTERVAL_MS = 350;
const MAX_POLL_FAILURES_BEFORE_REFRESH = 3;
const APP_BASE_URL = import.meta.env.BASE_URL;
const HOUMAO_ENTRY_FALLBACK_ICON_PATH = 'images/houmao-icon-blue-cut.png';
const ENTRY_DISPLAY_NAME_OVERRIDES: Record<string, string> = {
  bazi_expert: '张道士',
};

function storageKey(publicToken: string): string {
  return `wechat-permanent-entry:${publicToken}:viewer-token`;
}

function isIssueExpiredLocally(issue: WechatPermanentEntryIssue): boolean {
  const expiresAtMs = issue.expiresAt ? Date.parse(issue.expiresAt) : Number.NaN;
  return Number.isFinite(expiresAtMs) && expiresAtMs <= Date.now();
}

function issueStageFromExpiry(issue: WechatPermanentEntryIssue): IssueStage {
  if (issue.status === 'creating') {
    return 'creating';
  }
  if (issue.status === 'error') {
    return 'expired';
  }
  const expiresAtMs = issue.expiresAt ? Date.parse(issue.expiresAt) : Number.NaN;
  const remainingSeconds = Number.isFinite(expiresAtMs)
    ? Math.max(0, Math.ceil((expiresAtMs - Date.now()) / 1000))
    : null;

  if (issue.status === 'pending' && !issue.scanLink) {
    return 'creating';
  }
  if (issue.status === 'pending' && remainingSeconds !== null && remainingSeconds <= 0) {
    return 'expired';
  }

  return issue.status;
}

function mergeIssueStatus(
  issue: WechatPermanentEntryIssue,
  status: Awaited<ReturnType<typeof getWechatPermanentEntryIssue>>,
): WechatPermanentEntryIssue {
  const qrcode = status.qrcode ?? issue.qrcode;
  const scanLink = status.scanLink ?? issue.scanLink;
  const nextStatus = status.status === 'creating' && scanLink ? 'pending' : status.status;

  return {
    ...issue,
    qrcode,
    scanLink,
    status: nextStatus,
    expiresAt: status.expiresAt,
    attemptId: status.attemptId ?? issue.attemptId,
    invitationId: status.invitationId ?? issue.invitationId,
  };
}

function buildQrImageUrl(scanLink: string): string {
  const params = new URLSearchParams({
    size: '540',
    data: scanLink,
  });
  return `/api/v1/public/wechat/qr-image?${params.toString()}`;
}

function buildEntryAssetUrl(assetPath: string, baseUrl = APP_BASE_URL): string {
  const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
  const normalizedAssetPath = assetPath.startsWith('/') ? assetPath.slice(1) : assetPath;
  return `${normalizedBaseUrl}${normalizedAssetPath}`;
}

function sanitizeEntryAvatarPath(avatarUrl: string | null): string | null {
  if (typeof avatarUrl !== 'string') {
    return null;
  }

  const trimmed = avatarUrl.trim();
  if (!trimmed || !trimmed.startsWith('/images/')) {
    return null;
  }

  let decodedPath: string;
  try {
    decodedPath = decodeURIComponent(trimmed);
  } catch {
    return null;
  }

  if (decodedPath.includes('\\')) {
    return null;
  }

  const pathSegments = decodedPath.slice('/images/'.length).split('/');
  if (
    pathSegments.length === 0
    || pathSegments.some((segment) => !segment || segment === '.' || segment === '..')
  ) {
    return null;
  }

  return trimmed;
}

async function restoreViewerToken(publicToken: string): Promise<string> {
  const existingToken = sessionStorage.getItem(storageKey(publicToken));

  try {
    const session = await createWechatPermanentEntryViewerSession(publicToken, existingToken);
    sessionStorage.setItem(storageKey(publicToken), session.viewerToken);
    return session.viewerToken;
  } catch (error) {
    if (!existingToken) {
      throw error;
    }

    sessionStorage.removeItem(storageKey(publicToken));
    const session = await createWechatPermanentEntryViewerSession(publicToken);
    sessionStorage.setItem(storageKey(publicToken), session.viewerToken);
    return session.viewerToken;
  }
}

export default function WechatPermanentEntryPage() {
  const { publicToken = '' } = useParams();
  const expertShareConfig = getExpertShareConfigByPublicToken(publicToken);

  if (expertShareConfig) {
    return <NiuboshiExpertSharePage configOverride={expertShareConfig} />;
  }

  return <GenericWechatPermanentEntryPage publicToken={publicToken} />;
}

function GenericWechatPermanentEntryPage({ publicToken }: { publicToken: string }) {
  const [language] = useWebLanguage();
  const [entry, setEntry] = useState<PublicWechatPermanentEntry | null>(null);
  const [viewerToken, setViewerToken] = useState<string | null>(null);
  const [issue, setIssue] = useState<WechatPermanentEntryIssue | null>(null);
  const [stage, setStage] = useState<IssueStage>('idle');
  const [pageError, setPageError] = useState<PageErrorKey | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isIssuing, setIsIssuing] = useState(false);
  const [hasAutoIssued, setHasAutoIssued] = useState(false);
  const [pollFailureCount, setPollFailureCount] = useState(0);
  const [entryAvatarLoadFailed, setEntryAvatarLoadFailed] = useState(false);
  const issueRef = useRef<WechatPermanentEntryIssue | null>(null);
  const activeIssueId = issue?.issueId ?? null;
  const issueHasScanLink = Boolean(issue?.scanLink);

  useEffect(() => {
    issueRef.current = issue;
  }, [issue]);

  useEffect(() => {
    if (!publicToken) {
      setPageError('missingToken');
      setIsLoading(false);
      return;
    }

    let ignore = false;

    async function loadEntry(): Promise<void> {
      setIsLoading(true);
      setPageError(null);

      try {
        const [loadedEntry, loadedViewerToken] = await Promise.all([
          getPublicWechatPermanentEntry(publicToken),
          restoreViewerToken(publicToken),
        ]);

        if (ignore) {
          return;
        }

        setEntry(loadedEntry);
        setViewerToken(loadedViewerToken);
      } catch (loadError) {
        if (ignore) {
          return;
        }

        console.warn('wechat permanent entry load failed', loadError);
        setPageError('loadFailed');
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    }

    void loadEntry();
    return () => {
      ignore = true;
    };
  }, [publicToken]);

  useEffect(() => {
    setHasAutoIssued(false);
    setIssue(null);
    setStage('idle');
    setPollFailureCount(0);
  }, [publicToken]);

  useEffect(() => {
    if (!activeIssueId || (stage !== 'pending' && stage !== 'creating')) {
      return;
    }

    let ignore = false;

    const pollIssueStatus = () => {
      void (async () => {
        try {
          const nextStatus = await getWechatPermanentEntryIssue(activeIssueId);
          if (ignore) {
            return;
          }
          if (nextStatus.issueId !== activeIssueId) {
            return;
          }

          const currentIssue = issueRef.current;
          if (!currentIssue || currentIssue.issueId !== activeIssueId) {
            return;
          }

          const mergedIssue = mergeIssueStatus(currentIssue, nextStatus);
          issueRef.current = mergedIssue;
          setIssue(mergedIssue);
          setPollFailureCount(0);
          setStage(issueStageFromExpiry(mergedIssue));
        } catch (pollError) {
          if (ignore) {
            return;
          }
          setPollFailureCount((current) => {
            const nextCount = current + 1;
            if (nextCount >= MAX_POLL_FAILURES_BEFORE_REFRESH) {
              setStage('expired');
            }
            return nextCount;
          });
          console.warn('wechat permanent entry status poll failed', pollError);
        }
      })();
    };
    const isWaitingForQr = !issueHasScanLink;
    if (isWaitingForQr) {
      pollIssueStatus();
    }
    const timer = window.setInterval(
      pollIssueStatus,
      isWaitingForQr ? QR_READY_POLL_INTERVAL_MS : POLL_INTERVAL_MS,
    );

    return () => {
      ignore = true;
      window.clearInterval(timer);
    };
  }, [activeIssueId, issueHasScanLink, stage]);

  useEffect(() => {
    if (!issue || stage !== 'pending') {
      return;
    }

    if (isIssueExpiredLocally(issue)) {
      setStage('expired');
      return;
    }

    const timer = window.setInterval(() => {
      if (isIssueExpiredLocally(issue)) {
        setStage('expired');
      }
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [issue, stage]);

  const handleCreateIssue = useCallback(async (): Promise<void> => {
    if (!viewerToken) {
      setPageError('viewerNotReady');
      return;
    }

    setIsIssuing(true);
    setPageError(null);

    try {
      const createdIssue = await createWechatPermanentEntryIssue(publicToken, viewerToken);
      setIssue(createdIssue);
      setPollFailureCount(0);
      setStage(issueStageFromExpiry(createdIssue));
    } catch (issueError) {
      console.warn('wechat permanent entry issue creation failed', issueError);
    } finally {
      setIsIssuing(false);
    }
  }, [publicToken, viewerToken]);

  const isEntryAvailable = entry?.status === 'active' && entry.canIssue;
  const showUnavailableState =
    !isLoading && !pageError && entry !== null && !isEntryAvailable && stage !== 'confirmed';
  const allowManualRetry = stage === 'pending' && pollFailureCount >= MAX_POLL_FAILURES_BEFORE_REFRESH;
  const showRefreshAction =
    !showUnavailableState
    && !isLoading
    && viewerToken !== null
    && (stage === 'expired' || allowManualRetry);

  const copy = {
    zh: {
      langAria: '\u5207\u6362\u9875\u9762\u8bed\u8a00',
      wechatEntry: 'WECHAT \u5165\u53e3',
      wechatConnect: '\u5fae\u4fe1\u8fde\u63a5',
      loadingEntry: '\u6b63\u5728\u52a0\u8f7d\u6c38\u4e45\u5165\u53e3',
      unavailable: '\u5f53\u524d\u8fd9\u4e2a\u6c38\u4e45\u5165\u53e3\u6682\u65f6\u4e0d\u53ef\u7528\u4e8e\u65b0\u8fde\u63a5\u3002',
      missingToken: '\u7f3a\u5c11\u6c38\u4e45\u5165\u53e3\u6807\u8bc6\uff0c\u8bf7\u91cd\u8bd5\u3002',
      loadFailed: '\u6c38\u4e45\u5165\u53e3\u52a0\u8f7d\u5931\u8d25\uff0c\u8bf7\u5237\u65b0\u91cd\u8bd5\u3002',
      viewerNotReady: '\u5f53\u524d\u89c2\u770b\u4f1a\u8bdd\u8fd8\u6ca1\u51c6\u5907\u597d\uff0c\u8bf7\u7a0d\u540e\u518d\u8bd5\u3002',
      startConnection: '\u5f00\u59cb\u8fde\u63a5',
      refreshQr: '\u5237\u65b0\u4e8c\u7ef4\u7801',
      createNewBridge: '\u521b\u5efa\u65b0\u6865\u63a5',
      generatingQr: '\u6b63\u5728\u751f\u6210\u4e8c\u7ef4\u7801',
      tapToGenerate:
        '\u70b9\u51fb\u6309\u94ae\u5373\u53ef\u4e3a\u5f53\u524d\u5206\u8eab\u751f\u6210\u4e00\u6b21\u6027\u4e8c\u7ef4\u7801\u3002',
      qrAlt: 'iLink \u4e8c\u7ef4\u7801',
      qrScanHint: '\u957f\u6309\u8bc6\u522b\u8fde\u63a5',
      qrHelloHint:
        '\u8fdb\u5165\u804a\u5929\u540e\u8bb0\u5f97\u4e3b\u52a8\u53d1\u4e00\u53e5\u201c\u4f60\u597d\u201d\u3002',
      qrExpiryHint: '\u4e8c\u7ef4\u7801\u8fc7\u671f\u4f1a\u81ea\u52a8\u5237\u65b0',
      onlineStatus: '\u5728\u7ebf\u00a0\u00b7\u00a0\u968f\u65f6\u53ef\u804a',
      welcomePrefix: '\u6211\u662f',
      welcomeSuffix:
        '\uff0c\u8fdb\u5165\u804a\u5929\u540e\u5148\u53d1\u4e00\u53e5\u201c\u4f60\u597d\u201d\uff0c\u6211\u4f1a\u7ee7\u7eed\u5e2e\u4f60\u62c6\u89e3\u95ee\u9898\u3002\u6709\u4ec0\u4e48\u95ee\u9898\u8981\u95ee\u6211\u5417\uff1f',
    },
    en: {
      langAria: 'Switch page language',
      wechatEntry: 'WECHAT ENTRY',
      wechatConnect: 'WECHAT LINK',
      loadingEntry: 'Loading permanent entry',
      unavailable: 'This permanent entry is not available for new connections right now.',
      missingToken: 'Missing permanent entry token. Please retry.',
      loadFailed: 'Unable to load this entry. Please retry.',
      viewerNotReady: 'Viewer session is not ready yet.',
      startConnection: 'Start Connection',
      refreshQr: 'Refresh QR',
      createNewBridge: 'Create New Bridge',
      generatingQr: 'Generating QR code',
      tapToGenerate: 'Tap the button to generate a one-time iLink QR code for this avatar.',
      qrAlt: 'iLink QR code',
      qrScanHint: 'Long-press to connect',
      qrHelloHint: 'After entering chat, remember to send "hello" first.',
      qrExpiryHint: 'The QR code refreshes automatically after expiry',
      onlineStatus: 'Online\u00a0\u00b7\u00a0Ready to chat',
      welcomePrefix: 'I am ',
      welcomeSuffix: '. Send "hello" after entering chat, and I will help break down your question.',
    },
    pt: {
      langAria: 'Alternar idioma da pagina',
      wechatEntry: 'ENTRADA WECHAT',
      wechatConnect: 'LINK WECHAT',
      loadingEntry: 'Carregando entrada permanente',
      unavailable: 'Esta entrada permanente nao esta disponivel para novas conexoes agora.',
      missingToken: 'Token da entrada permanente ausente. Tente novamente.',
      loadFailed: 'Nao foi possivel carregar esta entrada. Tente novamente.',
      viewerNotReady: 'A sessao do visitante ainda nao esta pronta.',
      startConnection: 'Iniciar conexao',
      refreshQr: 'Atualizar QR',
      createNewBridge: 'Criar nova ponte',
      generatingQr: 'Gerando codigo QR',
      tapToGenerate: 'Toque no botao para gerar um QR iLink unico para este avatar.',
      qrAlt: 'Codigo QR iLink',
      qrScanHint: 'Pressione e segure para conectar',
      qrHelloHint: 'Ao entrar no chat, envie primeiro "ola".',
      qrExpiryHint: 'O QR e atualizado automaticamente ao expirar',
      onlineStatus: 'Online\u00a0\u00b7\u00a0Pronto para conversar',
      welcomePrefix: 'Eu sou ',
      welcomeSuffix: '. Envie "ola" ao entrar no chat, e eu ajudo a destrinchar sua pergunta.',
    },
  } as const;

  const t = copy[language];
  const entryDisplayName = entry
    ? isYoutuAgentProfile(entry.profile) ? YOUTU_BRAND_NAME : ENTRY_DISPLAY_NAME_OVERRIDES[entry.profile] ?? entry.displayName
    : t.loadingEntry;
  const entryAvatarPath = sanitizeEntryAvatarPath(entry?.avatarUrl ?? null);
  const entryAvatarCandidateSrc = entryAvatarPath ? buildEntryAssetUrl(entryAvatarPath) : null;
  const entryAvatarFallbackSrc = buildEntryAssetUrl(HOUMAO_ENTRY_FALLBACK_ICON_PATH);
  const entryAvatarSrc = entryAvatarCandidateSrc && !entryAvatarLoadFailed
    ? entryAvatarCandidateSrc
    : entryAvatarFallbackSrc;
  const pageStyle = {
    '--wechat-entry-avatar-bg': `url("${entryAvatarSrc}")`,
  } as CSSProperties;
  const entryWelcomeText = `${t.welcomePrefix}${entryDisplayName}${t.welcomeSuffix}`;

  useEffect(() => {
    setEntryAvatarLoadFailed(false);
  }, [entryAvatarCandidateSrc]);

  useEffect(() => {
    if (
      isLoading
      || hasAutoIssued
      || !isEntryAvailable
      || viewerToken === null
      || issue !== null
      || pageError !== null
      || isIssuing
      || stage !== 'idle'
    ) {
      return;
    }

    setHasAutoIssued(true);
    void handleCreateIssue();
  }, [
    handleCreateIssue,
    hasAutoIssued,
    isEntryAvailable,
    isIssuing,
    isLoading,
    issue,
    pageError,
    stage,
    viewerToken,
  ]);

  return (
    <main className="wechat-entry-page" style={pageStyle}>
      <div className="wechat-entry-shell">
        <section className="wechat-entry-panel" aria-label={t.wechatConnect}>
          <p className="wechat-entry-kicker">{t.wechatConnect}</p>
          {pageError ? <div className="wechat-entry-alert is-error">{t[pageError]}</div> : null}
          {showUnavailableState ? <div className="wechat-entry-alert is-error">{t.unavailable}</div> : null}

          {issue?.scanLink ? (
            <div className="wechat-entry-qr-card">
              <img
                alt={t.qrAlt}
                className="wechat-entry-qr-image"
                referrerPolicy="no-referrer"
                src={buildQrImageUrl(issue.scanLink)}
              />
            </div>
          ) : (
            <div className="wechat-entry-qr-empty">
              {stage === 'creating' || isIssuing ? t.generatingQr : t.tapToGenerate}
            </div>
          )}

          <p className="wechat-entry-qr-hint">{t.qrScanHint}</p>
          <p className="wechat-entry-qr-note">{t.qrHelloHint}</p>
          <p className="wechat-entry-qr-expiry">{t.qrExpiryHint}</p>

          {showRefreshAction ? (
            <div className="wechat-entry-action-row">
              <button
                className="wechat-entry-primary-btn"
                disabled={isIssuing || viewerToken === null}
                onClick={() => {
                  void handleCreateIssue();
                }}
                type="button"
              >
                {t.refreshQr}
              </button>
            </div>
          ) : null}
        </section>

        <section className="wechat-entry-profile" aria-label={entryDisplayName}>
          <div className="wechat-entry-message-row">
            <span className="wechat-entry-avatar-wrap">
              <img
                src={entryAvatarSrc}
                alt={entryDisplayName}
                className="wechat-entry-icon"
                loading="eager"
                decoding="async"
                onError={() => {
                  if (entryAvatarSrc !== entryAvatarFallbackSrc) {
                    setEntryAvatarLoadFailed(true);
                  }
                }}
              />
              <span className="wechat-entry-ai-badge">AI</span>
            </span>
            <p className="wechat-entry-speech">{entryWelcomeText}</p>
          </div>
          <h1 className="wechat-entry-profile-title">{entryDisplayName}</h1>
          <p className="wechat-entry-status-pill">
            <span aria-hidden="true" />
            {t.onlineStatus}
          </p>
        </section>
      </div>
    </main>
  );
}


